import { IonIcon } from '@ionic/react';
import {
  thumbsDown,
  thumbsDownOutline,
  thumbsUp,
  thumbsUpOutline,
} from 'ionicons/icons';
import React, { useState } from 'react';
import {
  ChatRating,
  isChatTelemetryConfigured,
  submitChatRating,
} from '../../services/ChatTelemetry.service';
import { AgentChatMessage, AgentRun } from './AgentChat.types';
import { finalAgentReplyIds } from './agentTurnReplies';

/**
 * Thumbs on the reply that closed a turn, so the team can see on the chat
 * dashboard whether answers actually helped. Hidden when telemetry is off.
 */

const GIVEN_KEY = 'chat-feedback-given';
const GIVEN_LIMIT = 200;

const readGiven = (): Record<string, ChatRating> => {
  try {
    const parsed = JSON.parse(localStorage.getItem(GIVEN_KEY) || '{}');
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, ChatRating>)
      : {};
  } catch {
    return {};
  }
};

const writeGiven = (messageId: string, rating: ChatRating): void => {
  const entries = Object.entries(readGiven()).filter(
    ([id]) => id !== messageId,
  );
  entries.push([messageId, rating]);
  localStorage.setItem(
    GIVEN_KEY,
    JSON.stringify(Object.fromEntries(entries.slice(-GIVEN_LIMIT))),
  );
};

interface AgentReplyFeedbackProps {
  run: AgentRun;
  message: AgentChatMessage;
}

export const AgentReplyFeedback: React.FC<AgentReplyFeedbackProps> = ({
  run,
  message,
}) => {
  const [given, setGiven] = useState<ChatRating | null>(
    () => readGiven()[message.id] ?? null,
  );

  if (!isChatTelemetryConfigured()) return null;
  if (message.role !== 'agent') return null;
  if (run.status === 'queued' || run.status === 'running') return null;
  if (!finalAgentReplyIds(run.messages).has(message.id)) return null;

  const rate = (rating: ChatRating) => {
    if (given === rating) return;
    setGiven(rating);
    writeGiven(message.id, rating);
    submitChatRating(run.id, message.id, rating);
  };

  return (
    <div className="agent-reply-feedback">
      <button
        type="button"
        className={`agent-reply-thumb${given === 'up' ? ' is-chosen' : ''}`}
        title="This helped"
        onClick={() => rate('up')}
      >
        <IonIcon icon={given === 'up' ? thumbsUp : thumbsUpOutline} />
      </button>
      <button
        type="button"
        className={`agent-reply-thumb is-down${
          given === 'down' ? ' is-chosen' : ''
        }`}
        title="This missed the mark"
        onClick={() => rate('down')}
      >
        <IonIcon icon={given === 'down' ? thumbsDown : thumbsDownOutline} />
      </button>
    </div>
  );
};

export default AgentReplyFeedback;
